import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  Pagination,
  SimpleGrid,
  Skeleton,
  Table,
  Text,
  createStyles,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronRight,
  IconExternalLink,
  IconListCheck,
  IconTrash,
} from "@tabler/icons-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import {
  clearSkypointOverdueTasks,
  getSkypointOverdueTasks,
} from "../../../services/skypointAlerts";
import { enterProviderView } from "../../../services/skypointProvider";

const LIMIT = 25;
const TOTAL_COLS = 4;

const useStyles = createStyles((theme) => ({
  container: { width: "100%" },
  stickyBanner: { position: "sticky", top: 0, zIndex: 100 },
  content: {
    padding: "24px 28px",
    [theme.fn.smallerThan("sm")]: { padding: "16px 12px" },
  },
  backRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#667085",
    cursor: "pointer",
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 12,
    "&:hover": { color: "#101828" },
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 20,
  },
  headerInfo: { flex: 1, minWidth: 0 },
  headerActions: { flexShrink: 0 },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: "#FFF6ED",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  title: {
    fontFamily: "Inter",
    fontSize: 22,
    fontWeight: 700,
    color: "#101828",
    lineHeight: 1.2,
  },
  subtitle: {
    fontFamily: "Inter",
    fontSize: 13,
    color: "#667085",
    marginTop: 2,
  },
  tableWrap: {
    background: "#FFFFFF",
    border: "1px solid #EAECF0",
    borderRadius: 12,
    overflow: "hidden",
  },
  table: {
    "& thead tr th": {
      backgroundColor: "#F9FAFB",
      fontFamily: "Inter",
      fontSize: 12,
      fontWeight: 600,
      color: "#667085",
      textTransform: "uppercase",
      letterSpacing: 0.4,
      borderBottom: "1px solid #EAECF0",
    },
    "& tbody tr td": {
      fontFamily: "Inter",
      fontSize: 13,
      color: "#101828",
      borderBottom: "1px solid #F2F4F7",
      verticalAlign: "middle",
    },
    "& tbody tr:last-of-type td": { borderBottom: "none" },
  },
  groupRow: {
    backgroundColor: "#F9FAFB",
    cursor: "pointer",
    userSelect: "none",
    "& td": {
      borderBottom: "1px solid #EAECF0 !important",
      padding: "10px 16px !important",
    },
    "&:hover td": {
      backgroundColor: "#F3F4F6",
    },
  },
  childRow: {
    "& td:first-of-type": {
      paddingLeft: "40px !important",
    },
  },
  emptyState: {
    padding: "48px 20px",
    textAlign: "center",
    color: "#667085",
    fontFamily: "Inter",
    fontSize: 14,
  },
  paginationRow: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "12px 16px",
    borderTop: "1px solid #EAECF0",
  },
}));

const OverdueTasks = ({ isSidebarOpen }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const { overdueTasks } = useSelector((state) => state?.skypointAlerts);

  const [page, setPage] = useState(1);
  const [clearing, setClearing] = useState(false);
  const [expandedProviders, setExpandedProviders] = useState(new Set());
  const [datesModalRow, setDatesModalRow] = useState(null);

  useEffect(() => {
    getSkypointOverdueTasks({ page, limit: LIMIT });
  }, [page]);

  useEffect(() => {
    setExpandedProviders(new Set());
  }, [page]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((overdueTasks.total || 0) / LIMIT)),
    [overdueTasks.total]
  );

  const items = useMemo(() => overdueTasks.items || [], [overdueTasks.items]);

  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.providerId || item.providerName;
      if (!map.has(key)) {
        map.set(key, {
          key,
          providerId: item.providerId,
          providerName: item.providerName,
          items: [],
        });
      }
      map.get(key).items.push(item);
    });
    return Array.from(map.values());
  }, [items]);

  const toggleProvider = (key) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleViewProvider = async (providerId, providerName) => {
    const ok = await enterProviderView(providerId, providerName);
    if (ok) navigate("/home/provider-view");
  };

  // Not overdueTasks.total — that counts staff rows, not missed tasks
  const missedCount = overdueTasks.missedTotal || 0;

  const handleClearAll = async () => {
    if (missedCount === 0) return;
    const confirmed = await swal({
      title: "Clear all overdue staff tasks?",
      text:
        `This will mark all ${missedCount} currently-overdue staff task ` +
        `entries across your providers as cleared. Future overdue tasks ` +
        `will still be detected normally.`,
      icon: "warning",
      buttons: ["Cancel", "Clear all"],
      dangerMode: true,
    });
    if (!confirmed) return;
    setClearing(true);
    const result = await clearSkypointOverdueTasks();
    setClearing(false);
    if (result) {
      await swal({
        title: "Cleared",
        text: `${result.totalCleared} staff-task entries cleared.`,
        icon: "success",
      });
      setPage(1);
      getSkypointOverdueTasks({ page: 1, limit: LIMIT });
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.stickyBanner}>
        <AppNavbar title="Home" profile={profile} isSidebarOpen={isSidebarOpen} />
      </Box>

      <Box className={classes.content}>
        <Box
          className={classes.backRow}
          onClick={() => navigate("/home/dashboard-skypoint")}
        >
          <IconArrowLeft size={16} />
          <span>Back to dashboard</span>
        </Box>

        <Box className={classes.headerRow}>
          <Box className={classes.headerIcon}>
            <IconListCheck size={22} color="#F79009" />
          </Box>
          <Box className={classes.headerInfo}>
            <Text className={classes.title}>Overdue Staff Tasks</Text>
            <Text className={classes.subtitle}>
              Staff tasks (Ogiso Core compliance tasks) past their due date, not
              yet completed
              {overdueTasks.total > 0 &&
                ` · ${missedCount} missed across ${overdueTasks.total} ${
                  overdueTasks.total === 1 ? "staff member" : "staff members"
                }`}
            </Text>
          </Box>
          <Box className={classes.headerActions}>
            <Button
              variant="light"
              color="red"
              leftIcon={<IconTrash size={16} />}
              loading={clearing}
              disabled={missedCount === 0 || clearing}
              onClick={handleClearAll}
            >
              Clear all
            </Button>
          </Box>
        </Box>

        <Box className={classes.tableWrap}>
          <Table className={classes.table} verticalSpacing="md">
            <thead>
              <tr>
                <th style={{ width: 220 }}>Staff Member</th>
                <th style={{ width: 110 }}>Missed</th>
                <th>Missed Dates</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {overdueTasks.loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={TOTAL_COLS}>
                      <Skeleton height={32} radius={6} />
                    </td>
                  </tr>
                ))
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan={TOTAL_COLS} className={classes.emptyState}>
                    No overdue staff tasks across your providers.
                  </td>
                </tr>
              ) : (
                groups.map((group) => {
                  const isExpanded = expandedProviders.has(group.key);
                  const groupMissed = group.items.reduce(
                    (sum, i) => sum + (i.missedCount || 0),
                    0
                  );
                  return (
                    <React.Fragment key={group.key}>
                      <tr
                        className={classes.groupRow}
                        onClick={() => toggleProvider(group.key)}
                      >
                        <td colSpan={TOTAL_COLS}>
                          <Flex align="center" gap={10}>
                            {isExpanded ? (
                              <IconChevronDown size={16} color="#667085" />
                            ) : (
                              <IconChevronRight size={16} color="#667085" />
                            )}
                            <Text weight={600} size="sm" color="#101828">
                              {group.providerName || "Unknown Provider"}
                            </Text>
                            <Badge size="sm" variant="filled" color="orange" radius="xl">
                              {group.items.length}{" "}
                              {group.items.length === 1 ? "staff member" : "staff members"}
                            </Badge>
                            {groupMissed > 0 && (
                              <Badge size="sm" variant="light" color="red" radius="xl">
                                {groupMissed} missed
                              </Badge>
                            )}
                          </Flex>
                        </td>
                      </tr>

                      {isExpanded &&
                        group.items.map((item) => {
                          const dates = item.dates || [];
                          const shown = dates.slice(0, 5);
                          const more = dates.length - shown.length;
                          return (
                            <tr key={item.relatedId} className={classes.childRow}>
                              <td>
                                <Text size="sm" color={item.staffName ? "dark" : "dimmed"}>
                                  {item.staffName || "— unassigned —"}
                                </Text>
                              </td>
                              <td>
                                <Badge
                                  color={
                                    item.missedCount >= 20
                                      ? "red"
                                      : item.missedCount >= 5
                                      ? "orange"
                                      : "yellow"
                                  }
                                  variant="light"
                                  size="sm"
                                >
                                  {item.missedCount}
                                </Badge>
                              </td>
                              <td>
                                <Text size="xs" color="dimmed">
                                  {shown.length === 0
                                    ? "—"
                                    : shown
                                        .map((d) => new Date(d).toLocaleDateString())
                                        .join(", ")}
                                  {more > 0 && (
                                    <>
                                      {" "}
                                      <Text
                                        component="span"
                                        size="xs"
                                        color="violet"
                                        sx={{
                                          cursor: "pointer",
                                          fontWeight: 500,
                                          "&:hover": { textDecoration: "underline" },
                                        }}
                                        onClick={() => setDatesModalRow(item)}
                                      >
                                        +{more} more
                                      </Text>
                                    </>
                                  )}
                                </Text>
                              </td>
                              <td>
                                <Button
                                  size="xs"
                                  variant="subtle"
                                  leftIcon={<IconExternalLink size={14} />}
                                  onClick={() =>
                                    handleViewProvider(item.providerId, item.providerName)
                                  }
                                >
                                  Ogiso Core
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
          {totalPages > 1 && (
            <Box className={classes.paginationRow}>
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
                size="sm"
              />
            </Box>
          )}
        </Box>
      </Box>

      <Modal
        opened={!!datesModalRow}
        onClose={() => setDatesModalRow(null)}
        title={
          datesModalRow
            ? `Missed dates — ${datesModalRow.staffName || "Unassigned"}`
            : ""
        }
        size="lg"
      >
        {datesModalRow && (
          <Box>
            <Text size="sm" color="dimmed" mb={16}>
              {datesModalRow.dates?.length || 0} missed{" "}
              {datesModalRow.dates?.length === 1 ? "date" : "dates"} at{" "}
              {datesModalRow.providerName}
            </Text>
            <SimpleGrid
              cols={3}
              spacing="sm"
              breakpoints={[
                { maxWidth: 700, cols: 2 },
                { maxWidth: 480, cols: 1 },
              ]}
            >
              {(datesModalRow.dates || []).map((d) => (
                <Badge key={d} variant="light" size="md" color="orange">
                  {new Date(d).toLocaleDateString(undefined, {
                    weekday: "short",
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </Badge>
              ))}
            </SimpleGrid>
          </Box>
        )}
      </Modal>
    </Box>
  );
};

export default OverdueTasks;
